import { StudentLayout } from "@jambushrushti/components/learning/StudentLayout";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  BookOpen,
  Clock,
  Play,
  CheckCircle2,
  Calendar,
  GraduationCap,
  ArrowRight,
  Trophy,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  useEnrollments,
  useCourses,
  type LMSEnrollment,
} from "@jambushrushti/hooks/useLMS";

import { format } from "date-fns";

export default function EnrolledCourses() {
  const { data: enrollments, isLoading: loadingEnrollments } = useEnrollments();
  const { data: courses, isLoading: loadingCourses } = useCourses();

  const isLoading = loadingEnrollments || loadingCourses;

  const displayCourses = ((enrollments ?? []) as LMSEnrollment[]).flatMap(
    enrollment => {
      const course = courses?.find(item => item.id === enrollment.course_id);
      if (!course) {
        return [];
      }
      return [
        {
          id: course.id,
          title: course.title,
          instructor: course.instructor_name || "Instructor not specified",
          thumbnail: course.thumbnail_url || "/placeholder.svg",
          enrolledAt: enrollment.enrolled_at,
          status:
            enrollment.status === "active" ? "in_progress" : enrollment.status,
        },
      ];
    }
  );

  const inProgressCourses = displayCourses.filter(
    c => c.status === "in_progress"
  );
  const completedCourses = displayCourses.filter(c => c.status === "completed");
  const notStartedCourses = displayCourses.filter(
    c => c.status === "not_started"
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="border-green-500/20 bg-green-500/10 text-green-600">
            Completed
          </Badge>
        );
      case "in_progress":
        return (
          <Badge className="border-primary/20 bg-primary/10 text-primary">
            In Progress
          </Badge>
        );
      default:
        return <Badge variant="secondary">Not Started</Badge>;
    }
  };

  const CourseCard = ({
    course,
  }: {
    course: (typeof displayCourses)[number];
  }) => (
    <Card className="group overflow-hidden border-primary/10 transition-all duration-300 hover:shadow-lg">
      <div className="relative h-40 overflow-hidden">
        <img
          src={course.thumbnail}
          alt={course.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          {getStatusBadge(course.status)}
          {course.status === "completed" && (
            <div className="rounded-full bg-green-500 p-1.5">
              <CheckCircle2 className="h-4 w-4 text-white" />
            </div>
          )}
        </div>
      </div>

      <CardContent className="space-y-4 p-4">
        <div>
          <h3 className="line-clamp-1 font-semibold text-foreground transition-colors group-hover:text-primary">
            {course.title}
          </h3>
          <p className="text-sm text-muted-foreground">{course.instructor}</p>
        </div>

        {/* Meta Info */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>
              Enrolled {format(new Date(course.enrolledAt), "MMM dd, yyyy")}
            </span>
          </div>
        </div>

        {/* Action Button */}
        <Button
          asChild
          className="w-full gap-2"
          variant={course.status === "completed" ? "outline" : "default"}
        >
          <Link to={`/learning/courses/${course.id}`}>
            {course.status === "completed" ? (
              <>
                <Trophy className="h-4 w-4" />
                View Certificate
              </>
            ) : course.status === "not_started" ? (
              <>
                <Play className="h-4 w-4" />
                Start Course
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Continue Learning
              </>
            )}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <StudentLayout title="My Enrolled Courses">
      <div className="space-y-8">
        {isLoading && (
          <p className="text-muted-foreground">Loading enrolled courses...</p>
        )}
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-background p-8">
          <div className="relative z-10">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-xl bg-primary/20 p-3">
                <GraduationCap className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-3xl font-bold text-foreground">
                  My Enrolled Courses
                </h1>
                <p className="text-muted-foreground">
                  Track your learning journey and continue where you left off
                </p>
              </div>
            </div>
          </div>
          <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card className="border-primary/10">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-primary/10 p-2">
                <BookOpen className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {displayCourses.length}
                </p>
                <p className="text-xs text-muted-foreground">Total Enrolled</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/10">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-blue-500/10 p-2">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {inProgressCourses.length}
                </p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/10">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-green-500/10 p-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {completedCourses.length}
                </p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/10">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-yellow-500/10 p-2">
                <Play className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {notStartedCourses.length}
                </p>
                <p className="text-xs text-muted-foreground">Not Started</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* In Progress Courses */}
        {inProgressCourses.length > 0 && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              <h2 className="font-display text-xl font-semibold text-foreground">
                Continue Learning
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {inProgressCourses.map(course => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          </div>
        )}

        {/* Not Started Courses */}
        {notStartedCourses.length > 0 && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <Play className="h-5 w-5 text-yellow-600" />
              <h2 className="font-display text-xl font-semibold text-foreground">
                Ready to Start
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {notStartedCourses.map(course => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          </div>
        )}

        {/* Completed Courses */}
        {completedCourses.length > 0 && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <h2 className="font-display text-xl font-semibold text-foreground">
                Completed
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {completedCourses.map(course => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && displayCourses.length === 0 && (
          <Card className="border-primary/10">
            <CardContent className="p-12 text-center">
              <div className="mx-auto mb-4 w-fit rounded-full bg-primary/10 p-4">
                <BookOpen className="h-12 w-12 text-primary" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-foreground">
                No Enrolled Courses Yet
              </h3>
              <p className="mx-auto mb-6 max-w-md text-muted-foreground">
                Start your learning journey by enrolling in courses that
                interest you.
              </p>
              <Button asChild>
                <Link to="/learning/courses" className="gap-2">
                  Browse Courses
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </StudentLayout>
  );
}
